import { createContext, lazy, ReactNode, Suspense, useCallback, useRef } from 'react'

import { FeatureFlagName, useFeatureFlags } from '@/infra/feature-flag'

const RecaptchaV2Provider = lazy(() =>
  import('./recaptcha-v2-provider').then(m => ({ default: m.RecaptchaV2Provider }))
)
const RecaptchaV3Provider = lazy(() =>
  import('./recaptcha-v3-provider').then(m => ({ default: m.RecaptchaV3Provider }))
)

export type RecaptchaCallback = () => Promise<unknown>

export interface RecaptchaContextType {
  executeRecaptcha: (callback: RecaptchaCallback, onError?: () => void) => Promise<void>
}

export const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined)

interface RecaptchaProviderProps {
  children: ReactNode
}

const RecaptchaDisabledProvider = ({ children }: RecaptchaProviderProps) => {
  const executeRecaptcha = useCallback(async (callback: RecaptchaCallback) => {
    await callback()
  }, [])

  return (
    <RecaptchaContext.Provider value={{ executeRecaptcha }}>{children}</RecaptchaContext.Provider>
  )
}

export const RecaptchaProvider = ({ children }: RecaptchaProviderProps) => {
  const { recaptchaEnabled, useRecaptchaV3 } = useFeatureFlags({
    recaptchaEnabled: FeatureFlagName.INVISIBLE_RECAPTCHA,
    useRecaptchaV3: FeatureFlagName.ENABLED_RECAPTCHA_V3
  })

  // Congela o modo de reCAPTCHA na primeira renderização.
  // Mudanças na flag refletem apenas no próximo carregamento da tela.
  const frozenModeRef = useRef<{ enabled: boolean; v3: boolean } | null>(null)
  if (frozenModeRef.current === null) {
    frozenModeRef.current = { enabled: recaptchaEnabled, v3: useRecaptchaV3 }
  }
  const { enabled, v3 } = frozenModeRef.current

  if (!enabled) {
    return <RecaptchaDisabledProvider>{children}</RecaptchaDisabledProvider>
  }

  if (v3) {
    return (
      <Suspense>
        <RecaptchaV3Provider>{children}</RecaptchaV3Provider>
      </Suspense>
    )
  }

  return (
    <Suspense>
      <RecaptchaV2Provider>{children}</RecaptchaV2Provider>
    </Suspense>
  )
}
